import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axiosInstance from './axiosInstance';
import './FunctionAssignmentStyles.css';
import './BulkGrabPushStoreInfo.css';

const UNWANTED_FIELDS = [
  'updatePageUnlock',
  'whiteListAutoInterval',
  'whitelistAutoInterval',
  'whiteListEnable',
  'acsEnable'
];

const storeConfigDisplayNames = {
  aliveInterval: 'Alive Interval',
  lostTime: 'Lost Time',
  logoDisplayTime: 'Logo Display Time',
  beaconLossLimit: 'Beacon Loss Limit',
  reactivationRetryLimit: 'Reactivation Retry Limit',
  scanPeriodSet: 'Scan Period Set',
  batteryMode: 'Battery Mode',
  location: 'Location',
  aesEnable: 'AES Enable',
  acsEnable: 'ACS Enable',
  whiteListEnable: 'White List Enable',
  tapGoStartTime: 'Tap Go Start Time',
  tapGoEndTime: 'Tap Go End Time',
  flexibleDefault: 'Flexible Default',
  rebootRetryCount: 'Reboot Retry Count',
  updatePageUnlock: 'Update Page Unlock',
  refreshStart: 'Refresh Start',
  ipTimeServer: 'IP Time Server',
  pageBackTime: 'Page Back Time',
  rfidEnable: 'RFID Enable',
  whitelistAutoInterval: 'Whitelist Auto Interval'
};

const getStoreConfigDisplayName = (key) =>
  storeConfigDisplayNames[key] ||
  key.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase());

// Build a short, readable label for a timezone like "EST (GMT-5)".
const getTzLabel = (tz) => {
  try {
    const now = new Date();
    const short = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      timeZoneName: 'short'
    })
      .formatToParts(now)
      .find((p) => p.type === 'timeZoneName')?.value || tz;
    const offset = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      timeZoneName: 'shortOffset'
    })
      .formatToParts(now)
      .find((p) => p.type === 'timeZoneName')?.value || '';
    // Drop offset if it duplicates the short name (e.g. "UTC (GMT)")
    return offset && offset !== short ? `${short} (${offset})` : short;
  } catch {
    return tz;
  }
};

const BROWSER_TZ =
  (typeof Intl !== 'undefined' && Intl.DateTimeFormat().resolvedOptions().timeZone) ||
  'America/New_York';

const COMMON_TZS = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Phoenix',
  'America/Los_Angeles',
  'America/Anchorage',
  'Pacific/Honolulu',
  'UTC'
];

// Browser tz first, then common tzs (de-duped).
const TZ_OPTIONS = [BROWSER_TZ, ...COMMON_TZS.filter((t) => t !== BROWSER_TZ)];

const parseValue = (val) => {
  if (typeof val !== 'string') return val;
  const lower = val.toLowerCase().trim();
  if (lower === 'true') return true;
  if (lower === 'false') return false;
  const num = parseInt(val, 10);
  if (!isNaN(num) && String(num) === val.trim()) return num;
  return val;
};

// SOLUM fields that MUST be booleans on the wire. Central rejects non-bool
// values with 405 WHITELISTE_ENABLE_MUST_BE_BOOLEAN etc; east is lenient
// and coerces internally. Sending real booleans keeps both regions happy.
const SOLUM_BOOLEAN_FIELDS = new Set([
  'whiteListEnable',
  'lbsEnabled',
  'aesEnable',
  'acsEnable',
  'rfidEnable'
]);

const toSolumBool = (val) => {
  if (typeof val === 'boolean') return val;
  if (val === 1 || val === '1' || val === 'true') return true;
  if (val === 0 || val === '0' || val === 'false') return false;
  return Boolean(val);
};

// Walks an object and coerces any known-boolean SOLUM field to a real boolean.
const coerceSolumBooleans = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  const out = { ...obj };
  Object.keys(out).forEach((k) => {
    if (SOLUM_BOOLEAN_FIELDS.has(k) && out[k] !== undefined && out[k] !== null) {
      out[k] = toSolumBool(out[k]);
    }
  });
  return out;
};

// SOLUM's PUT /store RESETS any omitted boolean storeConfig field to false
// (verified against COR/NJT: editing only refreshStart flipped whiteListEnable
// true -> false, while numeric fields like whitelistAutoInterval survived). So a
// delta-only storeConfig silently disables whitelist/lbs/POS/OTA/etc. Carry the
// current value of EVERY boolean field alongside the delta so none get reset.
const withStickyBooleans = (delta, currentConfig) => {
  const out = { ...(delta || {}) };
  const cur = currentConfig || {};
  Object.keys(cur).forEach((f) => {
    if (out[f] === undefined && typeof cur[f] === 'boolean') {
      out[f] = cur[f];
    }
  });
  return out;
};

/**
 * Reusable confirmation modal. Pass props via state, render once near root.
 * Closes on overlay click, Escape key, or Cancel button.
 */
const ConfirmModal = ({
  title,
  children,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'primary',
  onConfirm,
  onCancel
}) => {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onCancel();
      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) onConfirm();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onConfirm, onCancel]);

  // cancelLabel === null hides the cancel button (notice / acknowledgement mode).
  const showCancel = cancelLabel !== null;

  return (
    <div className="bg-modal-overlay" onClick={onCancel}>
      <div className="bg-modal" onClick={(e) => e.stopPropagation()}>
        <div className="bg-modal-header">{title}</div>
        <div className="bg-modal-body">{children}</div>
        <div className="bg-modal-footer">
          {showCancel && (
            <button className="bg-modal-btn-cancel" onClick={onCancel}>
              {cancelLabel}
            </button>
          )}
          <button
            className={`bg-modal-btn-confirm ${variant !== 'primary' ? variant : ''}`}
            onClick={onConfirm}
            autoFocus
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Standalone modal for collecting us-central SOLUM credentials.
 * Posts to /api/auth/saas-region-login on submit. On success the user's
 * saasTokenCentral fields are populated server-side.
 */
const CentralLoginModal = ({ onSuccess, onCancel }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && !submitting) onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel, submitting]);

  const submit = async (e) => {
    e?.preventDefault();
    if (!username || !password) {
      setError('Username and password are required.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await axiosInstance.post('/auth/saas-region-login', {
        region: 'central',
        username,
        password
      });
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Login failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="bg-modal-overlay"
      onClick={() => !submitting && onCancel()}
    >
      <form
        className="bg-modal"
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
      >
        <div className="bg-modal-header">Connect to US-Central</div>
        <div className="bg-modal-body">
          <p>Enter your US-Central credentials.</p>
          <div className="bg-modal-field">
            <label>Username</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoFocus
              autoComplete="off"
              disabled={submitting}
            />
          </div>
          <div className="bg-modal-field">
            <label>Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              disabled={submitting}
            />
          </div>
          {error && <div className="bg-modal-form-error">{error}</div>}
        </div>
        <div className="bg-modal-footer">
          <button
            type="button"
            className="bg-modal-btn-cancel"
            onClick={onCancel}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="bg-modal-btn-confirm"
            disabled={submitting}
          >
            {submitting ? 'Connecting…' : 'Connect'}
          </button>
        </div>
      </form>
    </div>
  );
};

const BulkGrabPushStoreInfo = () => {
  // SaaS region selector. 'east' is the historical default and matches the
  // existing user.saasToken realm. 'central' uses a separate token stored on
  // the user (saasTokenCentral) and is gated behind a credentials modal.
  const [region, setRegion] = useState('east');
  const [centralConnected, setCentralConnected] = useState(false);
  const [regionSwitching, setRegionSwitching] = useState(false);
  const [centralLoginOpen, setCentralLoginOpen] = useState(false);

  // On mount, probe whether the user already has a working central refresh token.
  useEffect(() => {
    let cancelled = false;
    axiosInstance
      .get('/auth/saas-region-status?region=central')
      .then((resp) => {
        if (!cancelled) setCentralConnected(!!resp.data?.connected);
      })
      .catch(() => {
        if (!cancelled) setCentralConnected(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Clears all company/store state so the new region starts fresh.
  // Defined as a callback because handleRegionChange and the credentials
  // modal both call it after switching.
  const resetForNewRegion = (nextRegion) => {
    setRegion(nextRegion);
    setAllCompanies([]);
    setSelectedLookupCompany(null);
    setStoreLookupSearch('');
    setShowSuggestions(false);
    setCompanySuggestions([]);
    setStoreConfigData([]);
    setOriginalStoreConfigs([]);
    setSelectedStores([]);
    setBulkUpdateValues({});
    setBulkUpdateBaseline({});
    setGrabResaveResults(null);
    setStoresError('');
    setCsvLoadResult(null);
  };

  const handleRegionChange = async (nextRegion) => {
    if (nextRegion === region) return;
    if (nextRegion === 'east') {
      resetForNewRegion('east');
      return;
    }
    // Switching to central — confirm we have a token first.
    setRegionSwitching(true);
    try {
      const resp = await axiosInstance.get('/auth/saas-region-status?region=central');
      const connected = !!resp.data?.connected;
      setCentralConnected(connected);
      if (connected) {
        resetForNewRegion('central');
      } else {
        // Open the credentials modal; on success we'll switch.
        setCentralLoginOpen(true);
      }
    } catch (err) {
      console.error('[region] status check failed:', err);
      setCentralLoginOpen(true);
    } finally {
      setRegionSwitching(false);
    }
  };

  const [allCompanies, setAllCompanies] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [companySuggestions, setCompanySuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [storeLookupSearch, setStoreLookupSearch] = useState('');
  const [selectedLookupCompany, setSelectedLookupCompany] = useState(null);

  const [loadingStores, setLoadingStores] = useState(false);
  const [storesError, setStoresError] = useState('');
  const [loadingConfigs, setLoadingConfigs] = useState(false);
  const [configLoadProgress, setConfigLoadProgress] = useState({ current: 0, total: 0 });

  const [storeConfigData, setStoreConfigData] = useState([]);
  const [originalStoreConfigs, setOriginalStoreConfigs] = useState([]);
  const [selectedStores, setSelectedStores] = useState([]);
  const [bulkUpdateValues, setBulkUpdateValues] = useState({});
  // Snapshot of bulkUpdateValues immediately after auto-population.
  // Used to distinguish "form was pre-filled" from "user actually edited."
  const [bulkUpdateBaseline, setBulkUpdateBaseline] = useState({});
  // Confirmation modal state. null = closed; object = open with these props.
  const [confirmModal, setConfirmModal] = useState(null);
  const [concurrentUpdating, setConcurrentUpdating] = useState(false);

  // Grab & Re-save state
  const [grabResaving, setGrabResaving] = useState(false);
  const [grabResaveProgress, setGrabResaveProgress] = useState({ current: 0, total: 0 });
  const [grabResaveResults, setGrabResaveResults] = useState(null);

  // Push history: { [storeCode]: pushRecord }
  const [pushHistory, setPushHistory] = useState({});
  const [pushedFilter, setPushedFilter] = useState('all'); // 'all' | 'last24h'

  // CSV import state
  const [csvLoadResult, setCsvLoadResult] = useState(null); // { error?, matched?, missing?, otherCompanies?, companyCode? }
  const csvInputRef = useRef(null);

  // Schedule tab state
  const [tab, setTab] = useState('runNow'); // 'runNow' | 'schedules'
  const [schedules, setSchedules] = useState([]);
  const [loadingSchedules, setLoadingSchedules] = useState(false);
  const [schedulesError, setSchedulesError] = useState('');
  const [runHistory, setRunHistory] = useState(null); // { scheduleId, runs }
  const [statusDetail, setStatusDetail] = useState(null); // schedule whose last-run status was clicked

  const emptyScheduleForm = {
    name: '',
    storeCodes: [],
    scheduleType: 'oneShot',
    runAtDate: '',
    runAtTime: '22:00',
    timezone: BROWSER_TZ
  };
  const [newSchedule, setNewSchedule] = useState(emptyScheduleForm);
  const [editingScheduleId, setEditingScheduleId] = useState(null);
  const [scheduleStoreSearch, setScheduleStoreSearch] = useState('');
  // Popover state for "Schedule these changes" launched from the Run Now tab.
  // null = closed. Otherwise: { name, scheduleType, runAtDate, runAtTime, timezone, edits }
  const [scheduleFromRunNow, setScheduleFromRunNow] = useState(null);

  const fetchAllCompanies = useCallback(async () => {
    if (allCompanies.length > 0) return allCompanies;
    try {
      setLoadingCompanies(true);
      setStoresError('');
      const response = await axiosInstance.get(`/companies/details?region=${region}`);
      if (response.data && response.data.companyList) {
        const companies = response.data.companyList
          .filter((c) => c.code && c.name)
          .map((c) => ({
            companyCode: c.code,
            companyName: c.name,
            id: c.id,
            currentstorecount: c.currentstorecount || 0
          }))
          .sort((a, b) => a.companyCode.localeCompare(b.companyCode));
        setAllCompanies(companies);
        return companies;
      }
      return [];
    } catch (err) {
      setStoresError('Failed to fetch companies: ' + (err.response?.data?.message || err.message));
      return [];
    } finally {
      setLoadingCompanies(false);
    }
  }, [allCompanies, region]);

  const searchCompanies = useCallback(
    async (searchText) => {
      if (!searchText || searchText.length === 0) {
        setCompanySuggestions([]);
        setShowSuggestions(false);
        return;
      }
      if (allCompanies.length === 0 && !loadingCompanies) {
        await fetchAllCompanies();
        return;
      }
      if (searchText.length < 2) {
        setCompanySuggestions([]);
        setShowSuggestions(false);
        return;
      }
      const searchLower = searchText.toLowerCase();
      const matches = allCompanies.filter(
        (c) =>
          c.companyCode.toLowerCase().includes(searchLower) ||
          c.companyName.toLowerCase().includes(searchLower)
      );
      setCompanySuggestions(matches.slice(0, 10));
      setShowSuggestions(matches.length > 0);
    },
    [allCompanies, loadingCompanies, fetchAllCompanies]
  );

  useEffect(() => {
    searchCompanies(storeLookupSearch);
  }, [storeLookupSearch, searchCompanies]);

  const handleInputFocus = async () => {
    let companies = allCompanies;
    if (allCompanies.length === 0 && !loadingCompanies) {
      companies = await fetchAllCompanies();
    }
    if (companies && companies.length > 0) {
      setCompanySuggestions(companies);
      setShowSuggestions(true);
    }
  };

  const fetchStoreConfigurations = async (companyCode, stores) => {
    try {
      setLoadingConfigs(true);
      setConfigLoadProgress({ current: 0, total: stores.length });

      const BATCH_SIZE = 5;
      const configResults = [];
      let completedCount = 0;

      for (let i = 0; i < stores.length; i += BATCH_SIZE) {
        const batch = stores.slice(i, i + BATCH_SIZE);
        const batchPromises = batch.map(async (store) => {
          try {
            const response = await axiosInstance.get(
              `/stores/${companyCode}/${store.store}/config?region=${region}`
            );
            if (response.data && response.data.storeConfig) {
              const cfg = { ...response.data.storeConfig };
              UNWANTED_FIELDS.forEach((f) => delete cfg[f]);
              Object.keys(cfg).forEach((k) => {
                cfg[k] = parseValue(cfg[k]);
              });
              completedCount++;
              setConfigLoadProgress({ current: completedCount, total: stores.length });
              return {
                storeCode: store.store,
                storeName: response.data.storeName || store.storeName,
                fullData: response.data,
                originalConfig: { ...cfg },
                ...cfg
              };
            }
            completedCount++;
            setConfigLoadProgress({ current: completedCount, total: stores.length });
            return null;
          } catch (err) {
            console.error(`Error fetching config for store ${store.store}:`, err.message);
            completedCount++;
            setConfigLoadProgress({ current: completedCount, total: stores.length });
            return null;
          }
        });
        const batchResults = await Promise.all(batchPromises);
        configResults.push(...batchResults.filter((r) => r !== null));
      }

      setStoreConfigData(configResults);
      setOriginalStoreConfigs(JSON.parse(JSON.stringify(configResults)));
    } catch (err) {
      console.error('Error fetching store configurations:', err);
    } finally {
      setLoadingConfigs(false);
      setConfigLoadProgress({ current: 0, total: 0 });
    }
  };

  const handleCompanySelectForLookup = async (company) => {
    setSelectedLookupCompany(company);
    setStoreLookupSearch('');
    setShowSuggestions(false);
    setCompanySuggestions([]);
    setStoreConfigData([]);
    setOriginalStoreConfigs([]);
    setSelectedStores([]);
    setBulkUpdateValues({});
    setBulkUpdateBaseline({});
    setGrabResaveResults(null);

    try {
      setLoadingStores(true);
      setStoresError('');
      const response = await axiosInstance.get(
        `/companies/stores?company=${company.companyCode}&region=${region}`
      );
      if (response.data && response.data.stores) {
        await Promise.all([
          fetchStoreConfigurations(company.companyCode, response.data.stores),
          fetchPushHistory(company.companyCode)
        ]);
      } else {
        setStoresError('No stores found for this company');
      }
    } catch (err) {
      setStoresError('Failed to fetch stores: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoadingStores(false);
    }
  };

  const clearCompanySelection = () => {
    setSelectedLookupCompany(null);
    setStoresError('');
    setStoreConfigData([]);
    setOriginalStoreConfigs([]);
    setSelectedStores([]);
    setBulkUpdateValues({});
    setBulkUpdateBaseline({});
    setGrabResaveResults(null);
    setCsvLoadResult(null);
  };

  const handleStoreSelection = (storeCode) => {
    setSelectedStores((prev) =>
      prev.includes(storeCode) ? prev.filter((s) => s !== storeCode) : [...prev, storeCode]
    );
  };

  const handleSelectAllStores = () => {
    if (selectedStores.length === storeConfigData.length) {
      setSelectedStores([]);
    } else {
      setSelectedStores(storeConfigData.map((s) => s.storeCode));
    }
  };

  // Auto-populate bulk fields when exactly one store is selected (matches original UX).
  // The baseline is set alongside so we can detect when the user has actually edited
  // away from the pre-populated values (vs the form just being non-empty).
  useEffect(() => {
    if (selectedStores.length === 1 && storeConfigData.length > 0) {
      const selected = storeConfigData.find((s) => s.storeCode === selectedStores[0]);
      if (selected && selected.fullData && selected.fullData.storeConfig) {
        const newValues = {};
        Object.keys(selected.fullData.storeConfig).forEach((key) => {
          const val = selected[key];
          if (typeof val === 'boolean' || key.toLowerCase().includes('enable')) {
            newValues[key] = val ? 1 : 0;
          } else {
            newValues[key] = val;
          }
        });
        setBulkUpdateValues(newValues);
        setBulkUpdateBaseline(newValues);
      }
    } else if (selectedStores.length !== 1) {
      setBulkUpdateValues({});
      setBulkUpdateBaseline({});
    }
  }, [selectedStores, storeConfigData]);

  // Opens the modal in notice mode — single OK button, no cancel.
  // Used for success/result/error messages that previously used window.alert.
  const showNotice = ({ title, body, variant = 'primary' }) => {
    setConfirmModal({
      title,
      children: body,
      confirmLabel: 'OK',
      cancelLabel: null,
      variant,
      onConfirm: () => setConfirmModal(null)
    });
  };

  // Renders a list item for a store with optional name annotation.
  const renderStoreLi = (code) => {
    const s = storeConfigData.find((sd) => sd.storeCode === code);
    const name = s?.storeName && s.storeName !== code ? s.storeName : null;
    return (
      <li key={code}>
        {code}
        {name && <span className="store-name"> — {name}</span>}
      </li>
    );
  };

  // Formats a config value for human-readable display in the confirm modal.
  const formatConfigValue = (key, val) => {
    if (val === '' || val === null || val === undefined) return '(empty)';
    if (key.toLowerCase().includes('enable')) {
      if (val === 1 || val === '1' || val === true) return 'Enabled';
      if (val === 0 || val === '0' || val === false) return 'Disabled';
    }
    return String(val);
  };

  const handleBulkStoreUpdate = () => {
    if (selectedStores.length === 0) {
      alert('Please select at least one store');
      return;
    }
    const hasValues = Object.keys(bulkUpdateValues).some(
      (k) => bulkUpdateValues[k] !== '' && bulkUpdateValues[k] !== null && bulkUpdateValues[k] !== undefined
    );
    if (!hasValues) {
      alert('Please set at least one configuration value to update');
      return;
    }

    // Compute fields that differ from baseline — these are what the user
    // actually wants to push. Pre-populated values that weren't edited
    // are filtered out so the confirm only lists real changes.
    const changedFields = {};
    Object.keys(bulkUpdateValues).forEach((key) => {
      const a = bulkUpdateValues[key];
      const b = bulkUpdateBaseline[key];
      const aEmpty = a === '' || a === null || a === undefined;
      const bEmpty = b === '' || b === null || b === undefined;
      if (aEmpty && bEmpty) return;
      if (aEmpty !== bEmpty || String(a) !== String(b)) {
        changedFields[key] = a;
      }
    });

    if (Object.keys(changedFields).length === 0) {
      alert('No changes to apply.');
      return;
    }

    setConfirmModal({
      title: `Apply changes to ${selectedStores.length} store(s)?`,
      children: (
        <>
          <div className="bg-modal-section">
            <h4>Stores ({selectedStores.length})</h4>
            <ul className="bg-modal-list">{selectedStores.map(renderStoreLi)}</ul>
          </div>
          <div className="bg-modal-section">
            <h4>Changes</h4>
            <ul className="bg-modal-list">
              {Object.keys(changedFields).map((k) => (
                <li key={k}>
                  <span className="key">{getStoreConfigDisplayName(k)}:</span>{' '}
                  <span className="val">{formatConfigValue(k, changedFields[k])}</span>
                </li>
              ))}
            </ul>
          </div>
        </>
      ),
      confirmLabel: 'Apply Changes',
      variant: 'primary',
      onConfirm: () => {
        setConfirmModal(null);
        executeBulkStoreUpdate();
      }
    });
  };

  const executeBulkStoreUpdate = async () => {
    try {
      setConcurrentUpdating(true);
      const BATCH_SIZE = 5;
      const allResults = [];

      for (let i = 0; i < selectedStores.length; i += BATCH_SIZE) {
        const batch = selectedStores.slice(i, i + BATCH_SIZE);
        const batchPromises = batch.map(async (storeCode) => {
          try {
            const storeData = storeConfigData.find((s) => s.storeCode === storeCode);
            if (!storeData || !storeData.fullData || !storeData.originalConfig) {
              return { success: false, storeCode, error: 'Store data not found' };
            }
            const fullData = storeData.fullData;
            const oldConfig = storeData.originalConfig;

            const configUpdates = {};
            Object.keys(bulkUpdateValues).forEach((key) => {
              const val = bulkUpdateValues[key];
              if (val !== '' && val !== null && val !== undefined) {
                configUpdates[key] = parseValue(val);
              }
            });
            const newConfig = { ...oldConfig, ...configUpdates };
            const changedFields = {};
            Object.keys(newConfig).forEach((k) => {
              if (oldConfig[k] !== newConfig[k]) changedFields[k] = newConfig[k];
            });

            if (Object.keys(changedFields).length === 0) {
              return { success: true, storeCode, skipped: true };
            }

            const payload = {
              address: fullData.address,
              city: fullData.city,
              country: fullData.country,
              region: fullData.region,
              storeName: fullData.storeName,
              zoneId: fullData.zoneId,
              ipNtpServer: fullData.ipNtpServer,
              whiteListEnable: toSolumBool(fullData.whiteListEnable),
              lbsEnabled: toSolumBool(fullData.lbsEnabled),
              store: storeCode,
              storeConfig: coerceSolumBooleans(withStickyBooleans(changedFields, oldConfig))
            };

            await axiosInstance.put(
              `/stores/${selectedLookupCompany.companyCode}/${storeCode}?region=${region}`,
              payload
            );

            setStoreConfigData((prev) =>
              prev.map((s) => {
                if (s.storeCode === storeCode) {
                  return {
                    ...s,
                    ...newConfig,
                    originalConfig: newConfig,
                    fullData: {
                      ...s.fullData,
                      storeConfig: { ...s.fullData.storeConfig, ...changedFields }
                    }
                  };
                }
                return s;
              })
            );

            return { success: true, storeCode };
          } catch (err) {
            return { success: false, storeCode, error: err.message };
          }
        });
        const batchResults = await Promise.all(batchPromises);
        allResults.push(...batchResults);
      }

      const successCount = allResults.filter((r) => r.success).length;
      const skippedCount = allResults.filter((r) => r.skipped).length;
      const failedCount = allResults.filter((r) => !r.success).length;

      if (failedCount === 0) {
        showNotice({
          title: 'Update complete',
          body: (
            <p>
              Successfully updated <strong>{successCount}</strong> store(s)
              {skippedCount > 0 && (
                <> &nbsp;<span style={{ color: '#94a3b8' }}>({skippedCount} had no changes)</span></>
              )}
              .
            </p>
          ),
          variant: 'success'
        });
        setSelectedStores([]);
        setBulkUpdateValues({});
        setBulkUpdateBaseline({});
      } else {
        const failedStores = allResults.filter((r) => !r.success);
        showNotice({
          title: `Updated ${successCount} of ${allResults.length} store(s)`,
          body: (
            <>
              <p>
                <strong>{successCount}</strong> succeeded,{' '}
                <strong style={{ color: '#ef4444' }}>{failedCount}</strong> failed.
              </p>
              <div className="bg-modal-section">
                <h4>Failed stores</h4>
                <ul className="bg-modal-list">
                  {failedStores.map((r) => (
                    <li key={r.storeCode}>
                      {r.storeCode}{' '}
                      <span className="store-name">— {r.error || 'unknown error'}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </>
          ),
          variant: 'danger'
        });
      }
    } catch (err) {
      console.error('Error updating stores:', err);
      showNotice({
        title: 'Update failed',
        body: <p>{err.message || 'Failed to update stores'}</p>,
        variant: 'danger'
      });
    } finally {
      setConcurrentUpdating(false);
    }
  };

  // Compute the typed field edits the user changed from the auto-populated baseline.
  const computeChangedEdits = () => {
    const edits = {};
    Object.keys(bulkUpdateValues).forEach((key) => {
      const a = bulkUpdateValues[key];
      const b = bulkUpdateBaseline[key];
      const aEmpty = a === '' || a === null || a === undefined;
      const bEmpty = b === '' || b === null || b === undefined;
      if (aEmpty && bEmpty) return;
      if (aEmpty !== bEmpty || String(a) !== String(b)) {
        edits[key] = parseValue(a);
      }
    });
    return edits;
  };

  const openScheduleFromRunNow = () => {
    if (!selectedLookupCompany) {
      alert('No company selected');
      return;
    }
    if (selectedStores.length === 0) {
      alert('Please select at least one store');
      return;
    }
    // Snapshot the edits at modal-open time. Unlike the immediate "Update Now" path
    // (executeBulkStoreUpdate), there is no per-store second-pass diff against live SaaS
    // data here — the scheduler applies these edits unconditionally when it fires, since
    // live config isn't known at schedule-creation time.
    const edits = computeChangedEdits();
    if (Object.keys(edits).length === 0) {
      alert('Set at least one configuration value to schedule.');
      return;
    }
    setScheduleFromRunNow({
      name: '',
      scheduleType: 'oneShot',
      runAtDate: '',
      runAtTime: '22:00',
      timezone: BROWSER_TZ,
      edits
    });
  };

  const submitScheduleFromRunNow = async () => {
    const f = scheduleFromRunNow;
    if (!selectedLookupCompany) return alert('No company selected');
    if (!f.name.trim()) return alert('Schedule name is required');
    const runAtIso = composeRunAt(f.runAtDate, f.runAtTime);
    if (!runAtIso) return alert('Valid date and time are required');
    if (selectedStores.length === 0) return alert('Select at least one store');
    try {
      await axiosInstance.post('/bulk-resave-schedules', {
        companyCode: selectedLookupCompany.companyCode,
        name: f.name.trim(),
        storeCodes: selectedStores,
        region,
        scheduleType: f.scheduleType,
        runAt: runAtIso,
        timezone: f.timezone,
        configEdits: f.edits
      });
      setScheduleFromRunNow(null);
      showNotice({
        title: 'Scheduled',
        body: <p>Schedule created. View it under the <strong>Schedules</strong> tab.</p>,
        variant: 'success'
      });
      if (tab === 'schedules') fetchSchedules(selectedLookupCompany.companyCode);
    } catch (err) {
      alert('Failed to create schedule: ' + (err.response?.data?.error || err.message));
    }
  };

  // The core of this new mode: for each selected store, re-fetch fresh from SaaS
  // and immediately PUT the full storeConfig back verbatim. No edits applied.
  const handleGrabAndResave = () => {
    if (selectedStores.length === 0) {
      alert('Please select at least one store');
      return;
    }
    if (!selectedLookupCompany) {
      alert('No company selected');
      return;
    }

    setConfirmModal({
      title: `Force Refresh ${selectedStores.length} store(s)?`,
      children: (
        <>
          <p>
            Re-pushes each selected store's current SaaS config unchanged. Use this
            to force SaaS to redistribute the config downstream without modifying
            any values.
          </p>
          <div className="bg-modal-section">
            <h4>Stores ({selectedStores.length})</h4>
            <ul className="bg-modal-list">{selectedStores.map(renderStoreLi)}</ul>
          </div>
        </>
      ),
      confirmLabel: 'Force Refresh',
      variant: 'primary',
      onConfirm: () => {
        setConfirmModal(null);
        executeGrabAndResave();
      }
    });
  };

  const executeGrabAndResave = async () => {
    try {
      setGrabResaving(true);
      setGrabResaveProgress({ current: 0, total: selectedStores.length });
      setGrabResaveResults(null);

      const companyCode = selectedLookupCompany.companyCode;
      const BATCH_SIZE = 5;
      const allResults = [];
      let completed = 0;

      for (let i = 0; i < selectedStores.length; i += BATCH_SIZE) {
        const batch = selectedStores.slice(i, i + BATCH_SIZE);
        const batchPromises = batch.map(async (storeCode) => {
          try {
            // Server-side GET+PUT (records the push automatically)
            await axiosInstance.post('/bulk-resave-schedules/grab-resave', {
              companyCode,
              storeCode,
              region
            });
            completed++;
            setGrabResaveProgress({ current: completed, total: selectedStores.length });
            return { success: true, storeCode };
          } catch (err) {
            completed++;
            setGrabResaveProgress({ current: completed, total: selectedStores.length });
            return {
              success: false,
              storeCode,
              error: err.response?.data?.error || err.message
            };
          }
        });
        const batchResults = await Promise.all(batchPromises);
        allResults.push(...batchResults);
      }

      // Refresh push history so the "Last Pushed" column updates
      await fetchPushHistory(companyCode);

      const successCount = allResults.filter((r) => r.success).length;
      const failed = allResults.filter((r) => !r.success);
      setGrabResaveResults({
        total: allResults.length,
        success: successCount,
        failed: failed.length,
        failedList: failed
      });
    } catch (err) {
      console.error('Grab & re-save error:', err);
      showNotice({
        title: 'Force Refresh failed',
        body: <p>{err.message || 'Unknown error'}</p>,
        variant: 'danger'
      });
    } finally {
      setGrabResaving(false);
      setGrabResaveProgress({ current: 0, total: 0 });
    }
  };

  // ---- CSV import ----
  // Parse a CSV file with rows: companycode;storecode (one pair per line, optional header).
  // Returns { byCompany: { [code]: storeCodes[] }, totalRows }.
  const parseCsvText = (text) => {
    const lines = text
      .replace(/\r/g, '')
      .split('\n')
      .map((l) => l.replace(/^\uFEFF/, '').trim())
      .filter((l) => l.length > 0);
    if (lines.length === 0) return { byCompany: {}, totalRows: 0 };

    let dataLines = lines;
    // Skip a header row that looks like "companycode;storecode"
    if (/^company/i.test(lines[0])) dataLines = lines.slice(1);

    const byCompany = {};
    dataLines.forEach((line) => {
      const parts = line.split(';').map((c) => c.trim()).filter(Boolean);
      if (parts.length < 2) return;
      const [co, st] = parts;
      if (!byCompany[co]) byCompany[co] = [];
      if (!byCompany[co].includes(st)) byCompany[co].push(st);
    });
    return { byCompany, totalRows: dataLines.length };
  };

  // Shared CSV validation: returns { error } or { matched, missing, otherCompanies, ... }
  const validateCsvForCurrentCompany = async (file) => {
    if (!file) return null;
    if (!selectedLookupCompany) {
      return { error: 'Select a company before loading a CSV.' };
    }
    if (storeConfigData.length === 0) {
      return { error: 'Stores are still loading — try again in a moment.' };
    }

    let text = '';
    try {
      text = await file.text();
    } catch (err) {
      return { error: 'Failed to read file: ' + err.message };
    }

    const { byCompany } = parseCsvText(text);
    const companyCodes = Object.keys(byCompany);
    if (companyCodes.length === 0) {
      return { error: 'No valid rows found (expected: companycode;storecode)' };
    }

    const selectedCode = selectedLookupCompany.companyCode;
    const wanted = byCompany[selectedCode] || [];
    const otherCompanies = companyCodes.filter((c) => c !== selectedCode);

    if (wanted.length === 0) {
      return {
        error: `No rows in CSV match selected company "${selectedCode}". Found: ${companyCodes.join(', ')}`
      };
    }

    const present = new Set(storeConfigData.map((s) => s.storeCode));
    const matched = wanted.filter((c) => present.has(c));
    const missing = wanted.filter((c) => !present.has(c));
    return {
      matched,
      missing,
      otherCompanies,
      companyCode: selectedCode,
      csvRowCount: wanted.length
    };
  };

  const handleCsvLoad = async (file) => {
    setCsvLoadResult(null);
    const result = await validateCsvForCurrentCompany(file);
    if (!result) return;
    setCsvLoadResult(result);
    if (result.matched) {
      // Apply to BOTH Run Now selection and the schedule form's store list
      setSelectedStores(result.matched);
      setNewSchedule((s) => ({ ...s, storeCodes: result.matched }));
    }
  };

  // ---- Push history ----
  const fetchPushHistory = useCallback(async (companyCode) => {
    if (!companyCode) return;
    try {
      const resp = await axiosInstance.get(
        `/bulk-resave-schedules/push-history?companyCode=${encodeURIComponent(companyCode)}`
      );
      const byStore = {};
      (resp.data.records || []).forEach((r) => {
        byStore[r.storeCode] = r;
      });
      setPushHistory(byStore);
    } catch (err) {
      console.error('Failed to load push history:', err.message);
    }
  }, []);

  // Pretty short relative time: "2m ago", "3h ago", "4d ago"
  const fmtRelative = (iso) => {
    if (!iso) return '—';
    const then = new Date(iso).getTime();
    const diff = Date.now() - then;
    if (diff < 60 * 1000) return 'just now';
    if (diff < 60 * 60 * 1000) return `${Math.floor(diff / (60 * 1000))}m ago`;
    if (diff < 24 * 60 * 60 * 1000) return `${Math.floor(diff / (60 * 60 * 1000))}h ago`;
    return `${Math.floor(diff / (24 * 60 * 60 * 1000))}d ago`;
  };

  const filteredStoreConfigData = useMemo(() => {
    if (pushedFilter === 'all') return storeConfigData;
    if (pushedFilter === 'last24h') {
      const cutoff = Date.now() - 24 * 60 * 60 * 1000;
      return storeConfigData.filter((s) => {
        const rec = pushHistory[s.storeCode];
        return rec && new Date(rec.lastPushedAt).getTime() >= cutoff;
      });
    }
    return storeConfigData;
  }, [storeConfigData, pushedFilter, pushHistory]);

  // ---- Schedules ----
  const fetchSchedules = useCallback(async (companyCode) => {
    if (!companyCode) return;
    try {
      setLoadingSchedules(true);
      setSchedulesError('');
      const resp = await axiosInstance.get(
        `/bulk-resave-schedules?companyCode=${encodeURIComponent(companyCode)}`
      );
      setSchedules(resp.data.schedules || []);
    } catch (err) {
      setSchedulesError(err.response?.data?.error || err.message);
    } finally {
      setLoadingSchedules(false);
    }
  }, []);

  useEffect(() => {
    if (tab === 'schedules' && selectedLookupCompany) {
      fetchSchedules(selectedLookupCompany.companyCode);
    }
  }, [tab, selectedLookupCompany, fetchSchedules]);

  // Convert local date + time + timezone into a UTC Date (ISO string).
  // We build a "YYYY-MM-DDTHH:MM:00" string and let the browser interpret it
  // in the user's locale. For the server we just need an absolute Date.
  // NOTE: this treats the picker input as local-browser time, not the typed
  // timezone. The typed timezone is stored for display only. If a user picks
  // a different timezone, the server interprets runAt as the absolute UTC
  // timestamp it represents, which is what node-cron needs anyway.
  const composeRunAt = (dateStr, timeStr) => {
    if (!dateStr || !timeStr) return null;
    const combined = `${dateStr}T${timeStr}:00`;
    const d = new Date(combined);
    if (isNaN(d.getTime())) return null;
    return d.toISOString();
  };

  const resetScheduleForm = () => {
    setNewSchedule(emptyScheduleForm);
    setEditingScheduleId(null);
    setScheduleStoreSearch('');
  };

  const loadScheduleIntoForm = (schedule) => {
    const d = new Date(schedule.runAt);
    const pad = (n) => String(n).padStart(2, '0');
    const dateStr = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    const timeStr = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
    setNewSchedule({
      name: schedule.name,
      storeCodes: schedule.storeCodes || [],
      scheduleType: schedule.scheduleType,
      runAtDate: dateStr,
      runAtTime: timeStr,
      timezone: schedule.timezone || 'America/New_York'
    });
    setEditingScheduleId(schedule._id);
  };

  const saveSchedule = async () => {
    if (!selectedLookupCompany) {
      alert('Select a company first');
      return;
    }
    if (!newSchedule.name.trim()) return alert('Schedule name is required');
    if (newSchedule.storeCodes.length === 0) return alert('Select at least one store');
    const runAtIso = composeRunAt(newSchedule.runAtDate, newSchedule.runAtTime);
    if (!runAtIso) return alert('Pick a valid date and time');

    const payload = {
      companyCode: selectedLookupCompany.companyCode,
      name: newSchedule.name.trim(),
      storeCodes: newSchedule.storeCodes,
      region,
      scheduleType: newSchedule.scheduleType,
      runAt: runAtIso,
      timezone: newSchedule.timezone
    };

    try {
      if (editingScheduleId) {
        await axiosInstance.put(`/bulk-resave-schedules/${editingScheduleId}`, payload);
      } else {
        await axiosInstance.post('/bulk-resave-schedules', payload);
      }
      resetScheduleForm();
      fetchSchedules(selectedLookupCompany.companyCode);
    } catch (err) {
      alert('Failed to save schedule: ' + (err.response?.data?.error || err.message));
    }
  };

  const toggleScheduleEnabled = async (schedule) => {
    try {
      await axiosInstance.put(`/bulk-resave-schedules/${schedule._id}`, {
        enabled: !schedule.enabled
      });
      fetchSchedules(selectedLookupCompany.companyCode);
    } catch (err) {
      alert('Failed: ' + (err.response?.data?.error || err.message));
    }
  };

  const deleteSchedule = async (scheduleId) => {
    if (!window.confirm('Delete this schedule?')) return;
    try {
      await axiosInstance.delete(`/bulk-resave-schedules/${scheduleId}`);
      fetchSchedules(selectedLookupCompany.companyCode);
    } catch (err) {
      alert('Failed: ' + (err.response?.data?.error || err.message));
    }
  };

  const runScheduleNow = async (scheduleId) => {
    if (!window.confirm('Run this schedule immediately?')) return;
    try {
      await axiosInstance.post(`/bulk-resave-schedules/${scheduleId}/run-now`);
      alert('Run started. Check history in a minute to see results.');
    } catch (err) {
      alert('Failed: ' + (err.response?.data?.error || err.message));
    }
  };

  const viewRunHistory = async (schedule) => {
    try {
      const resp = await axiosInstance.get(`/bulk-resave-schedules/${schedule._id}/runs`);
      setRunHistory({ schedule, runs: resp.data.runs || [] });
    } catch (err) {
      alert('Failed to load history: ' + (err.response?.data?.error || err.message));
    }
  };

  const toggleScheduleStore = (storeCode) => {
    setNewSchedule((prev) => ({
      ...prev,
      storeCodes: prev.storeCodes.includes(storeCode)
        ? prev.storeCodes.filter((s) => s !== storeCode)
        : [...prev.storeCodes, storeCode]
    }));
  };

  const exportStoreConfigToCSV = () => {
    if (!storeConfigData || storeConfigData.length === 0) {
      alert('No store configuration data to export');
      return;
    }
    const configKeys = storeConfigData[0].fullData?.storeConfig
      ? Object.keys(storeConfigData[0].fullData.storeConfig)
      : [];
    if (configKeys.length === 0) {
      alert('No configuration fields available to export');
      return;
    }
    const headers = ['Store Code', 'Store Name', ...configKeys.map((k) => getStoreConfigDisplayName(k))];
    const rows = storeConfigData.map((store) => {
      const row = [store.storeCode, store.storeName || ''];
      configKeys.forEach((k) => {
        const v = store[k];
        if (typeof v === 'boolean') row.push(v ? 'Enabled' : 'Disabled');
        else if (v === 1 || v === 0) row.push(v === 1 ? 'Enabled' : 'Disabled');
        else row.push(v !== null && v !== undefined ? v : '');
      });
      return row;
    });
    const csvContent = [
      headers.map((h) => `"${String(h).replace(/"/g, '""')}"`).join(','),
      ...rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','))
    ].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    const companyCode = selectedLookupCompany?.companyCode || 'store-config';
    link.setAttribute('href', url);
    link.setAttribute(
      'download',
      `${companyCode}-store-config-${new Date().toISOString().split('T')[0]}.csv`
    );
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="function-assignment-container bulk-grab-root">
      <header className="function-assignment-header">
        <h1>Bulk Grab &amp; Push Store Info</h1>
        <p>Fetch SaaS store configs and re-push them (optionally with edits) across multiple stores</p>
      </header>

      <div className="store-lookup-container">
        {/* Region selector — east is the default; central requires a one-time
            credentials prompt the first time it's selected. */}
        <div className="region-selector-row">
          <label className="region-selector-label">SaaS Region:</label>
          <select
            className="region-selector-select"
            value={region}
            onChange={(e) => handleRegionChange(e.target.value)}
            disabled={regionSwitching || loadingCompanies || loadingStores || loadingConfigs}
          >
            <option value="east">US-East</option>
            <option value="central">US-Central{centralConnected ? '' : ' (not connected)'}</option>
          </select>
          {region === 'central' && (
            <span className={`region-status-pill ${centralConnected ? 'connected' : 'disconnected'}`}>
              {centralConnected ? '● connected' : '● not connected'}
            </span>
          )}
          {regionSwitching && (
            <span className="region-status-pill" style={{ background: '#1e3a5f', color: '#93c5fd' }}>
              switching…
            </span>
          )}
        </div>

        {/* Company-not-found banner stays at top so it's visible even when no stores are loaded */}
        {csvLoadResult?.error && (
          <div
            style={{
              background: '#3f1d1d',
              color: '#fca5a5',
              padding: '10px 14px',
              borderRadius: '6px',
              borderLeft: '4px solid #ef4444',
              marginBottom: '10px'
            }}
          >
            {csvLoadResult.error}
          </div>
        )}

        <div className="store-lookup-search-wrapper">
          <div className="store-lookup-search">
            <div className="search-input-wrapper">
              <input
                type="text"
                placeholder="Type company code or name (e.g., WFC, Walmart)..."
                value={selectedLookupCompany ? '' : storeLookupSearch}
                onChange={(e) => setStoreLookupSearch(e.target.value.toUpperCase())}
                onFocus={handleInputFocus}
                className="store-lookup-input"
                disabled={loadingStores || loadingConfigs || selectedLookupCompany}
              />
              {selectedLookupCompany && (
                <div className="selected-company-overlay">
                  <span className="selected-company-text">
                    {selectedLookupCompany.companyName} ({selectedLookupCompany.companyCode})
                  </span>
                  <button
                    className="clear-company-x-inside"
                    onClick={clearCompanySelection}
                    title="Clear selection"
                  >
                    ✕
                  </button>
                </div>
              )}
            </div>
          </div>

          {showSuggestions && companySuggestions.length > 0 && !selectedLookupCompany && (
            <div className="store-suggestions-dropdown">
              {companySuggestions.map((company) => (
                <div
                  key={company.companyCode}
                  className="store-suggestion-item"
                  onClick={() => handleCompanySelectForLookup(company)}
                >
                  <div className="suggestion-store-code">{company.companyCode}</div>
                  <div className="suggestion-store-name">{company.companyName}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {loadingStores && (
          <div className="loading-message">
            Loading stores for {selectedLookupCompany?.companyName}...
          </div>
        )}

        {storesError && <div className="store-lookup-error">{storesError}</div>}

        {loadingConfigs && (
          <div className="loading-message">
            Loading store configurations... {configLoadProgress.current}/{configLoadProgress.total}
          </div>
        )}

        {selectedLookupCompany && storeConfigData.length > 0 && !loadingConfigs && (
          <div className="store-batch-updater">
            <div
              className="tab-container"
              style={{ marginBottom: '12px', display: 'flex', alignItems: 'center' }}
            >
              <button
                className={`tab-button ${tab === 'runNow' ? 'active' : ''}`}
                onClick={() => setTab('runNow')}
              >
                Run Now
              </button>
              <button
                className={`tab-button ${tab === 'schedules' ? 'active' : ''}`}
                onClick={() => setTab('schedules')}
              >
                Schedules {schedules.length > 0 ? `(${schedules.length})` : ''}
              </button>
              {tab === 'runNow' && (
                <button
                  className="export-csv-button"
                  onClick={exportStoreConfigToCSV}
                  title="Export current store configurations to CSV"
                  style={{ marginLeft: 'auto' }}
                >
                  Export Current Setup
                </button>
              )}
            </div>

            {tab === 'runNow' && (
            <>

            {/* Editable bulk-update inputs */}
            <div className="config-fields-grid">
              {storeConfigData[0].fullData &&
                storeConfigData[0].fullData.storeConfig &&
                Object.keys(storeConfigData[0].fullData.storeConfig).map((configKey) => {
                  const sampleValue = storeConfigData[0][configKey];
                  const isBoolean =
                    typeof sampleValue === 'boolean' || configKey.toLowerCase().includes('enable');
                  return (
                    <div key={configKey} className="config-field-item">
                      <label>{getStoreConfigDisplayName(configKey)}:</label>
                      {isBoolean ? (
                        <select
                          value={bulkUpdateValues[configKey] ?? ''}
                          onChange={(e) =>
                            setBulkUpdateValues((prev) => ({
                              ...prev,
                              [configKey]: e.target.value === '' ? '' : parseInt(e.target.value)
                            }))
                          }
                        >
                          <option value="">-- Select --</option>
                          <option value="1">Enabled</option>
                          <option value="0">Disabled</option>
                        </select>
                      ) : typeof sampleValue === 'string' ? (
                        <input
                          type="text"
                          value={bulkUpdateValues[configKey] ?? ''}
                          onChange={(e) =>
                            setBulkUpdateValues((prev) => ({
                              ...prev,
                              [configKey]: e.target.value
                            }))
                          }
                          placeholder="Enter value..."
                        />
                      ) : (
                        <input
                          type="number"
                          value={bulkUpdateValues[configKey] ?? ''}
                          onChange={(e) =>
                            setBulkUpdateValues((prev) => ({
                              ...prev,
                              [configKey]: e.target.value === '' ? '' : parseInt(e.target.value)
                            }))
                          }
                          placeholder="Enter value..."
                        />
                      )}
                    </div>
                  );
                })}
            </div>

            {/* Action buttons */}
            <div className="batch-action-buttons">
              {(() => {
                // hasChanges is true when bulkUpdateValues differ from the baseline
                // (the auto-populated snapshot, or {} for 0/multi-select).
                // Treats '', null, undefined as equivalent (empty) so clearing a
                // field that was never populated isn't counted as a change.
                const hasChanges = (() => {
                  const allKeys = new Set([
                    ...Object.keys(bulkUpdateValues),
                    ...Object.keys(bulkUpdateBaseline)
                  ]);
                  for (const key of allKeys) {
                    const a = bulkUpdateValues[key];
                    const b = bulkUpdateBaseline[key];
                    const aEmpty = a === '' || a === null || a === undefined;
                    const bEmpty = b === '' || b === null || b === undefined;
                    if (aEmpty && bEmpty) continue;
                    if (aEmpty !== bEmpty) return true;
                    if (String(a) !== String(b)) return true;
                  }
                  return false;
                })();
                return hasChanges ? (
                  <button
                    className="update-now-button"
                    onClick={handleBulkStoreUpdate}
                    disabled={concurrentUpdating || grabResaving || selectedStores.length === 0}
                  >
                    {concurrentUpdating ? 'Updating...' : 'Update Now'}
                  </button>
                ) : (
                  <button
                    className="update-now-button"
                    style={{ background: '#0ea5e9' }}
                    onClick={handleGrabAndResave}
                    disabled={grabResaving || concurrentUpdating || selectedStores.length === 0}
                    title="Re-pushes each store's current config to SaaS unchanged. Use when you want SaaS to redistribute the config downstream without modifying any values."
                  >
                    {grabResaving
                      ? `Force Refreshing... ${grabResaveProgress.current}/${grabResaveProgress.total}`
                      : 'Force Refresh'}
                  </button>
                );
              })()}

              <button
                type="button"
                className="update-now-button"
                style={{ background: '#8b5cf6' }}
                onClick={openScheduleFromRunNow}
                disabled={concurrentUpdating || grabResaving || selectedStores.length === 0}
                title="Save the current field changes as a one-time or daily schedule instead of applying now."
              >
                Schedule these changes
              </button>

              <button
                type="button"
                className="update-now-button"
                style={{ background: '#3b82f6' }}
                onClick={() => csvInputRef.current?.click()}
                title="Load a CSV (companycode;storecode per line) to bulk-select stores for this company"
              >
                Load CSV
              </button>
              <input
                ref={csvInputRef}
                type="file"
                accept=".csv,text/csv"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleCsvLoad(f);
                  e.target.value = '';
                }}
              />
              <span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>
                Format:{' '}
                <code style={{ background: '#0f172a', padding: '1px 6px', borderRadius: '3px' }}>
                  companycode;storecode
                </code>
              </span>

              {!concurrentUpdating && !grabResaving && selectedStores.length > 0 && (
                <span className="status-message-inline">
                  {selectedStores.length} store(s) selected
                </span>
              )}
              {concurrentUpdating && (
                <span className="status-message-inline updating">Updating stores...</span>
              )}
              {grabResaving && (
                <span className="status-message-inline updating">
                  Force refreshing...
                </span>
              )}
            </div>

            {grabResaveResults && (
              <div
                className={
                  grabResaveResults.failed === 0
                    ? 'loading-message'
                    : 'store-lookup-error'
                }
                style={{ marginTop: '12px' }}
              >
                Force Refresh complete: {grabResaveResults.success}/{grabResaveResults.total} succeeded
                {grabResaveResults.failed > 0 && (
                  <div style={{ marginTop: '6px' }}>
                    Failed: {grabResaveResults.failedList.map((f) => `${f.storeCode} (${f.error})`).join(', ')}
                  </div>
                )}
              </div>
            )}

            {/* CSV import summary */}
            {csvLoadResult && !csvLoadResult.error && (csvLoadResult.matched || csvLoadResult.missing) && (
              <div style={{ marginTop: '10px' }}>
                <div
                  style={{
                    background: '#1e293b',
                    color: '#e2e8f0',
                    padding: '10px 14px',
                    borderRadius: '6px',
                    borderLeft: '4px solid #38bdf8'
                  }}
                >
                  {(() => {
                    const stillChecked = csvLoadResult.matched.filter((c) =>
                      selectedStores.includes(c)
                    ).length;
                    return (
                      <>
                        CSV applied for <strong>{csvLoadResult.companyCode}</strong>:{' '}
                        <strong style={{ color: '#38bdf8' }}>{stillChecked}</strong> of{' '}
                        <strong>{csvLoadResult.matched.length}</strong> store(s) currently checked,{' '}
                        <strong
                          style={{
                            color: csvLoadResult.missing.length > 0 ? '#fca5a5' : '#94a3b8'
                          }}
                        >
                          {csvLoadResult.missing.length}
                        </strong>{' '}
                        not found.
                      </>
                    );
                  })()}
                  {csvLoadResult.otherCompanies && csvLoadResult.otherCompanies.length > 0 && (
                    <div style={{ marginTop: '4px', color: '#94a3b8', fontSize: '0.85rem' }}>
                      Skipped rows for other companies: {csvLoadResult.otherCompanies.join(', ')}
                    </div>
                  )}
                </div>
                {csvLoadResult.missing.length > 0 && (
                  <div
                    style={{
                      marginTop: '6px',
                      background: '#3f3a1d',
                      color: '#fde68a',
                      padding: '10px 14px',
                      borderRadius: '6px',
                      borderLeft: '4px solid #eab308'
                    }}
                  >
                    <strong>Stores in CSV but not in this company:</strong>
                    <div
                      style={{
                        fontFamily: 'monospace',
                        marginTop: '6px',
                        fontSize: '0.85rem',
                        wordBreak: 'break-all'
                      }}
                    >
                      {csvLoadResult.missing.join(', ')}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Filter bar */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: '10px 0' }}>
              <label style={{ color: '#94a3b8', fontWeight: 600 }}>Filter:</label>
              <select
                value={pushedFilter}
                onChange={(e) => setPushedFilter(e.target.value)}
                style={{
                  background: '#0f172a',
                  color: '#e2e8f0',
                  border: '1px solid #334155',
                  borderRadius: '4px',
                  padding: '6px 10px'
                }}
              >
                <option value="all">All stores</option>
                <option value="last24h">Pushed in last 24h</option>
              </select>
              <span style={{ color: '#94a3b8', fontSize: '0.85rem' }}>
                Showing {filteredStoreConfigData.length} of {storeConfigData.length}
              </span>
            </div>

            {/* Store table */}
            <div className="store-batch-table-wrapper">
              <table className="store-batch-table">
                <thead>
                  <tr>
                    <th style={{ width: '40px' }}>
                      <input
                        type="checkbox"
                        checked={
                          selectedStores.length === storeConfigData.length &&
                          storeConfigData.length > 0
                        }
                        onChange={handleSelectAllStores}
                      />
                    </th>
                    <th>Store</th>
                    <th>Last Pushed</th>
                    {storeConfigData[0].fullData &&
                      storeConfigData[0].fullData.storeConfig &&
                      Object.keys(storeConfigData[0].fullData.storeConfig).map((k) => (
                        <th key={k}>{getStoreConfigDisplayName(k)}</th>
                      ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredStoreConfigData.map((store) => (
                    <tr
                      key={store.storeCode}
                      className={selectedStores.includes(store.storeCode) ? 'selected-row' : ''}
                    >
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedStores.includes(store.storeCode)}
                          onChange={() => handleStoreSelection(store.storeCode)}
                        />
                      </td>
                      <td>{store.storeCode}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {(() => {
                          const rec = pushHistory[store.storeCode];
                          if (!rec) return <span style={{ color: '#64748b' }}>never</span>;
                          const label = fmtRelative(rec.lastPushedAt);
                          const src =
                            rec.source === 'schedule'
                              ? ` · ${rec.scheduleName || 'schedule'}`
                              : ' · manual';
                          return (
                            <span title={new Date(rec.lastPushedAt).toLocaleString()}>
                              {label}
                              <span style={{ color: '#64748b', fontSize: '0.8rem' }}>{src}</span>
                            </span>
                          );
                        })()}
                      </td>
                      {store.fullData &&
                        store.fullData.storeConfig &&
                        Object.keys(store.fullData.storeConfig).map((k) => {
                          const v = store[k];
                          const isBoolean =
                            typeof v === 'boolean' || k.toLowerCase().includes('enable');
                          return (
                            <td key={k}>
                              {isBoolean
                                ? v === 1 || v === true
                                  ? 'Enabled'
                                  : 'Disabled'
                                : v !== null && v !== undefined
                                ? v
                                : ''}
                            </td>
                          );
                        })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </>
            )}

            {tab === 'schedules' && (
            <div className="schedules-tab" style={{ marginTop: '8px' }}>
              {/* Create / Edit schedule form */}
              <div style={{ padding: '16px', border: '1px solid #e5e7eb', borderRadius: '8px', marginBottom: '16px' }}>
                <h3 style={{ marginTop: 0 }}>
                  {editingScheduleId ? 'Edit Schedule' : 'New Schedule'}
                </h3>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', marginBottom: '12px' }}>
                  <div>
                    <label style={{ display: 'block', fontWeight: 600, marginBottom: '4px' }}>Name</label>
                    <input
                      type="text"
                      value={newSchedule.name}
                      onChange={(e) => setNewSchedule((s) => ({ ...s, name: e.target.value }))}
                      placeholder="e.g. Nightly overnight batch"
                      style={{ width: '100%', padding: '6px 8px' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontWeight: 600, marginBottom: '4px' }}>Type</label>
                    <select
                      value={newSchedule.scheduleType}
                      onChange={(e) => setNewSchedule((s) => ({ ...s, scheduleType: e.target.value }))}
                      style={{ width: '100%', padding: '6px 8px' }}
                    >
                      <option value="oneShot">One-shot (run once)</option>
                      <option value="daily">Daily (recurring)</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontWeight: 600, marginBottom: '4px' }}>Date</label>
                    <input
                      type="date"
                      value={newSchedule.runAtDate}
                      onChange={(e) => setNewSchedule((s) => ({ ...s, runAtDate: e.target.value }))}
                      style={{ width: '100%', padding: '6px 8px' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontWeight: 600, marginBottom: '4px' }}>Time (24h)</label>
                    <input
                      type="time"
                      value={newSchedule.runAtTime}
                      onChange={(e) => setNewSchedule((s) => ({ ...s, runAtTime: e.target.value }))}
                      style={{ width: '100%', padding: '6px 8px' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontWeight: 600, marginBottom: '4px' }}>Timezone</label>
                    <select
                      value={newSchedule.timezone}
                      onChange={(e) => setNewSchedule((s) => ({ ...s, timezone: e.target.value }))}
                      style={{ width: '100%', padding: '6px 8px' }}
                    >
                      {TZ_OPTIONS.map((tz) => (
                        <option key={tz} value={tz}>
                          {getTzLabel(tz)}
                          {tz === BROWSER_TZ ? ' — your timezone' : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div style={{ marginBottom: '12px' }}>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: '4px' }}>
                    Stores ({newSchedule.storeCodes.length} selected)
                  </label>
                  <input
                    type="text"
                    placeholder="Filter stores..."
                    value={scheduleStoreSearch}
                    onChange={(e) => setScheduleStoreSearch(e.target.value)}
                    style={{ width: '100%', padding: '6px 8px', marginBottom: '6px' }}
                  />
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '6px' }}>
                    <button
                      type="button"
                      onClick={() =>
                        setNewSchedule((s) => ({
                          ...s,
                          storeCodes: storeConfigData.map((st) => st.storeCode)
                        }))
                      }
                    >
                      Select all ({storeConfigData.length})
                    </button>
                    <button
                      type="button"
                      onClick={() => setNewSchedule((s) => ({ ...s, storeCodes: [] }))}
                    >
                      Clear
                    </button>
                    {selectedStores.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setNewSchedule((s) => ({ ...s, storeCodes: [...selectedStores] }))}
                        title="Use the stores currently checked in the Run Now tab"
                      >
                        Use Run Now selection ({selectedStores.length})
                      </button>
                    )}
                  </div>

                  {csvLoadResult && !csvLoadResult.error && csvLoadResult.matched && (
                    <div
                      style={{
                        marginBottom: '8px',
                        color: '#94a3b8',
                        fontSize: '0.8rem',
                        fontStyle: 'italic'
                      }}
                    >
                      Last CSV loaded ({csvLoadResult.matched.length} stores from{' '}
                      {csvLoadResult.companyCode}) — click "Load CSV" in Run Now to replace.
                    </div>
                  )}
                  <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #e5e7eb', padding: '6px', borderRadius: '4px' }}>
                    {storeConfigData
                      .filter((st) => {
                        if (!scheduleStoreSearch) return true;
                        const q = scheduleStoreSearch.toLowerCase();
                        return (
                          String(st.storeCode).toLowerCase().includes(q) ||
                          String(st.storeName || '').toLowerCase().includes(q)
                        );
                      })
                      .map((st) => (
                        <label key={st.storeCode} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '2px 0' }}>
                          <input
                            type="checkbox"
                            checked={newSchedule.storeCodes.includes(st.storeCode)}
                            onChange={() => toggleScheduleStore(st.storeCode)}
                          />
                          <span>
                            <strong>{st.storeCode}</strong>
                            {st.storeName ? ` — ${st.storeName}` : ''}
                          </span>
                        </label>
                      ))}
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '8px' }}>
                  <button className="update-now-button" onClick={saveSchedule}>
                    {editingScheduleId ? 'Update Schedule' : 'Create Schedule'}
                  </button>
                  {editingScheduleId && (
                    <button onClick={resetScheduleForm}>Cancel edit</button>
                  )}
                </div>
              </div>

              {/* Existing schedules list */}
              <h3>Existing Schedules</h3>
              {loadingSchedules && <div className="loading-message">Loading schedules...</div>}
              {schedulesError && <div className="store-lookup-error">{schedulesError}</div>}
              {!loadingSchedules && schedules.length === 0 && (
                <div style={{ color: '#6b7280' }}>No schedules for this company yet.</div>
              )}
              {schedules.length > 0 && (
                <table className="store-batch-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Type</th>
                      <th>Effect</th>
                      <th>Next Run</th>
                      <th>Stores</th>
                      <th>Last Run</th>
                      <th>Status</th>
                      <th>Enabled</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {schedules.map((sch) => (
                      <tr key={sch._id}>
                        <td>{sch.name}</td>
                        <td>{sch.scheduleType}</td>
                        <td>
                          {sch.configEdits && Object.keys(sch.configEdits).length > 0
                            ? `${Object.keys(sch.configEdits).length} field edit(s)`
                            : 'Force refresh'}
                        </td>
                        <td>{sch.nextRunAt ? new Date(sch.nextRunAt).toLocaleString() : '—'}</td>
                        <td>{sch.storeCodes?.length || 0}</td>
                        <td>{sch.lastRunAt ? new Date(sch.lastRunAt).toLocaleString() : '—'}</td>
                        <td>
                          {sch.lastRunStatus ? (
                            <button
                              type="button"
                              className="status-detail-link"
                              data-status={sch.lastRunStatus}
                              onClick={() => setStatusDetail(sch)}
                              title="Click to see why"
                            >
                              {sch.lastRunStatus}
                            </button>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td>
                          <input
                            type="checkbox"
                            checked={!!sch.enabled}
                            onChange={() => toggleScheduleEnabled(sch)}
                          />
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          <button onClick={() => runScheduleNow(sch._id)}>Run Now</button>{' '}
                          <button onClick={() => viewRunHistory(sch)}>History</button>{' '}
                          <button onClick={() => loadScheduleIntoForm(sch)}>Edit</button>{' '}
                          <button onClick={() => deleteSchedule(sch._id)}>Delete</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {runHistory && (
                <div
                  style={{
                    position: 'fixed',
                    inset: 0,
                    background: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000
                  }}
                  onClick={() => setRunHistory(null)}
                >
                  <div
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      background: '#fff',
                      padding: '20px',
                      borderRadius: '8px',
                      maxWidth: '800px',
                      width: '90%',
                      maxHeight: '80vh',
                      overflowY: 'auto'
                    }}
                  >
                    <h3 style={{ marginTop: 0 }}>Run History — {runHistory.schedule.name}</h3>
                    {runHistory.runs.length === 0 && <div>No runs yet.</div>}
                    {runHistory.runs.length > 0 && (
                      <table className="store-batch-table">
                        <thead>
                          <tr>
                            <th>Started</th>
                            <th>Triggered</th>
                            <th>Token User</th>
                            <th>Success</th>
                            <th>Failed</th>
                            <th>Error</th>
                          </tr>
                        </thead>
                        <tbody>
                          {runHistory.runs.map((r) => (
                            <tr key={r._id}>
                              <td>{new Date(r.startedAt).toLocaleString()}</td>
                              <td>{r.triggeredBy}</td>
                              <td>{r.tokenUser || '—'}</td>
                              <td>{r.successCount}</td>
                              <td>{r.failedCount}</td>
                              <td style={{ maxWidth: '300px', wordBreak: 'break-word' }}>
                                {r.error ||
                                  (r.failedStores?.length > 0
                                    ? r.failedStores.map((f) => `${f.storeCode}: ${f.error}`).join('; ')
                                    : '')}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                    <div style={{ marginTop: '12px', textAlign: 'right' }}>
                      <button onClick={() => setRunHistory(null)}>Close</button>
                    </div>
                  </div>
                </div>
              )}

              {statusDetail && (
                <div
                  style={{
                    position: 'fixed',
                    inset: 0,
                    background: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000
                  }}
                  onClick={() => setStatusDetail(null)}
                >
                  <div
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      background: '#fff',
                      padding: '20px',
                      borderRadius: '8px',
                      maxWidth: '700px',
                      width: '90%',
                      maxHeight: '80vh',
                      overflowY: 'auto'
                    }}
                  >
                    <h3 style={{ marginTop: 0 }}>
                      Last Run — {statusDetail.name}{' '}
                      <span className="status-detail-link" data-status={statusDetail.lastRunStatus} style={{ cursor: 'default' }}>
                        {statusDetail.lastRunStatus}
                      </span>
                    </h3>
                    <div style={{ color: '#374151', marginBottom: '10px' }}>
                      Ran {statusDetail.lastRunAt ? new Date(statusDetail.lastRunAt).toLocaleString() : '—'}
                      {statusDetail.lastRunSummary && typeof statusDetail.lastRunSummary.total === 'number' && (
                        <> · {statusDetail.lastRunSummary.success}/{statusDetail.lastRunSummary.total} stores succeeded</>
                      )}
                    </div>

                    {statusDetail.lastRunStatus === 'success' && (
                      <div style={{ color: '#16a34a' }}>All stores pushed successfully — no errors.</div>
                    )}

                    {/* Top-level failure (e.g. token resolution) */}
                    {statusDetail.lastRunSummary?.error && (
                      <div style={{ color: '#b91c1c', marginBottom: '10px' }}>
                        <strong>Error:</strong> {statusDetail.lastRunSummary.error}
                      </div>
                    )}

                    {/* Per-store failures with exact SaaS output */}
                    {statusDetail.lastRunSummary?.failedStores?.length > 0 && (
                      <>
                        <div style={{ fontWeight: 600, marginBottom: '6px' }}>
                          Failed stores ({statusDetail.lastRunSummary.failedStores.length}):
                        </div>
                        <table className="store-batch-table">
                          <thead>
                            <tr>
                              <th>Store</th>
                              <th>Exact error</th>
                            </tr>
                          </thead>
                          <tbody>
                            {statusDetail.lastRunSummary.failedStores.map((f) => (
                              <tr key={f.storeCode}>
                                <td style={{ whiteSpace: 'nowrap' }}>{f.storeCode}</td>
                                <td style={{ wordBreak: 'break-word' }}>{f.error}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </>
                    )}

                    {/* Raw summary for the full, exact output */}
                    {statusDetail.lastRunSummary && (
                      <>
                        <div style={{ fontWeight: 600, margin: '12px 0 6px' }}>Raw output:</div>
                        <pre
                          style={{
                            background: '#0f172a',
                            color: '#e2e8f0',
                            padding: '12px',
                            borderRadius: '6px',
                            overflowX: 'auto',
                            fontSize: '12px',
                            margin: 0
                          }}
                        >
                          {JSON.stringify(statusDetail.lastRunSummary, null, 2)}
                        </pre>
                      </>
                    )}

                    {!statusDetail.lastRunSummary && statusDetail.lastRunStatus !== 'success' && (
                      <div style={{ color: '#6b7280' }}>No detail recorded for this run.</div>
                    )}

                    <div style={{ marginTop: '12px', textAlign: 'right' }}>
                      <button onClick={() => setStatusDetail(null)}>Close</button>
                    </div>
                  </div>
                </div>
              )}
            </div>
            )}
          </div>
        )}
      </div>

      {confirmModal && (
        <ConfirmModal
          title={confirmModal.title}
          confirmLabel={confirmModal.confirmLabel}
          cancelLabel={confirmModal.cancelLabel}
          variant={confirmModal.variant}
          onConfirm={confirmModal.onConfirm}
          onCancel={() => setConfirmModal(null)}
        >
          {confirmModal.children}
        </ConfirmModal>
      )}

      {scheduleFromRunNow && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
          }}
          onClick={() => setScheduleFromRunNow(null)}
        >
          <div
            className="bg-modal"
            style={{ background: '#0f172a', padding: '20px', borderRadius: '8px', minWidth: '380px', maxWidth: '480px' }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginTop: 0 }}>Schedule these changes</h3>
            <div className="bg-modal-section">
              <h4>Stores ({selectedStores.length})</h4>
              <ul className="bg-modal-list">{selectedStores.map(renderStoreLi)}</ul>
            </div>
            <div className="bg-modal-section">
              <h4>Changes ({Object.keys(scheduleFromRunNow.edits).length})</h4>
              <ul className="bg-modal-list">
                {Object.keys(scheduleFromRunNow.edits).map((k) => (
                  <li key={k}>
                    <span className="key">{getStoreConfigDisplayName(k)}:</span>{' '}
                    <span className="val">{formatConfigValue(k, scheduleFromRunNow.edits[k])}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div style={{ display: 'grid', gap: '8px', marginTop: '8px' }}>
              <label>Schedule name
                <input
                  type="text"
                  value={scheduleFromRunNow.name}
                  onChange={(e) => setScheduleFromRunNow((s) => ({ ...s, name: e.target.value }))}
                />
              </label>
              <label>Type
                <select
                  value={scheduleFromRunNow.scheduleType}
                  onChange={(e) => setScheduleFromRunNow((s) => ({ ...s, scheduleType: e.target.value }))}
                >
                  <option value="oneShot">One time</option>
                  <option value="daily">Daily</option>
                </select>
              </label>
              <label>Date
                <input
                  type="date"
                  value={scheduleFromRunNow.runAtDate}
                  onChange={(e) => setScheduleFromRunNow((s) => ({ ...s, runAtDate: e.target.value }))}
                />
              </label>
              <label>Time
                <input
                  type="time"
                  value={scheduleFromRunNow.runAtTime}
                  onChange={(e) => setScheduleFromRunNow((s) => ({ ...s, runAtTime: e.target.value }))}
                />
              </label>
              <div style={{ fontSize: '0.8rem', color: '#94a3b8' }}>
                Runs at the entered time in your timezone ({scheduleFromRunNow.timezone}).
              </div>
            </div>
            <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
              <button className="update-now-button" onClick={submitScheduleFromRunNow}>
                Create Schedule
              </button>
              <button type="button" onClick={() => setScheduleFromRunNow(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {centralLoginOpen && (
        <CentralLoginModal
          onCancel={() => setCentralLoginOpen(false)}
          onSuccess={() => {
            setCentralLoginOpen(false);
            setCentralConnected(true);
            resetForNewRegion('central');
          }}
        />
      )}
    </div>
  );
};

export default BulkGrabPushStoreInfo;
